import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type SoundKey = 'notification' | 'alert'; // Extend as needed

const SOUND_FILES: Record<SoundKey, string> = {
  notification: '/assets/3.mp3',
  alert: '/assets/4.mp3',
};

const SOUND_KEYS = Object.keys(SOUND_FILES) as SoundKey[];

@Injectable({ providedIn: 'root' })
export class SoundService implements OnDestroy {
  private audioContext: AudioContext | null = null;
  private audioBuffers: Partial<Record<SoundKey, AudioBuffer>> = {};
  private isUnlocked = false;
  private pendingQueue: SoundKey[] = [];
  private permissionSubject = new BehaviorSubject<boolean | null>(null);
  public readonly permission$: Observable<boolean | null> = this.permissionSubject.asObservable();
  private gestureListening = false;
  private unlockInFlight: Promise<boolean> | null = null;

  /** Capture-phase so resume() stays inside a trusted user gesture. */
  private readonly onGesture = (): void => {
    this.beginUnlockFromGesture();
  };

  constructor() {
    const stored = localStorage.getItem('soundPermissionGranted');
    if (stored === 'true') this.permissionSubject.next(true);
    else if (stored === 'false') this.permissionSubject.next(false);
    else this.permissionSubject.next(null);

    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === 'soundPermissionGranted') {
        this.emitPermissionFromStorage();
      }
    });

    if (this.permissionSubject.value === true) {
      this.ensureGestureListeners();
    }
  }

  ngOnDestroy(): void {
    this.removeGestureListeners();
  }

  /**
   * Attempt unlock (must run during a user gesture to succeed).
   * Prefer the automatic document listeners; this remains for explicit calls.
   */
  async handleUserInteraction(): Promise<void> {
    if (this.permissionSubject.value === true && !this.isUnlocked) {
      await this.beginUnlockFromGesture();
    }
  }

  /**
   * Persist permission. When granted during a UI click (Enable Sounds / settings),
   * unlock runs in that same gesture so playback works immediately.
   */
  setPermission(granted: boolean): void {
    localStorage.setItem('soundPermissionGranted', granted ? 'true' : 'false');
    this.permissionSubject.next(granted);
    if (granted) {
      void this.beginUnlockFromGesture({ playConfirm: true });
    } else {
      this.lockAudio();
    }
  }

  /**
   * Play a sound by key. Queues until AudioContext is running after a user gesture.
   */
  async playSound(key: SoundKey): Promise<void> {
    if (this.permissionSubject.value !== true) return;

    if (!this.isUnlocked) {
      this.pendingQueue.push(key);
      this.ensureGestureListeners();
      return;
    }

    try {
      await this.ensureContextRunning();
      await this.playBuffer(key);
    } catch {
      // Browser re-suspended audio (e.g. after backgrounding) — wait for next gesture.
      this.isUnlocked = false;
      this.pendingQueue.push(key);
      this.ensureGestureListeners();
    }
  }

  get currentPermission(): boolean | null {
    return this.permissionSubject.value;
  }

  private lockAudio(): void {
    this.isUnlocked = false;
    this.pendingQueue.length = 0;
    this.removeGestureListeners();
    if (this.audioContext) {
      void this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }
    this.audioBuffers = {};
  }

  private ensureGestureListeners(): void {
    if (this.gestureListening || this.isUnlocked || this.permissionSubject.value !== true) {
      return;
    }
    this.gestureListening = true;
    document.addEventListener('pointerdown', this.onGesture, true);
    document.addEventListener('keydown', this.onGesture, true);
  }

  private removeGestureListeners(): void {
    if (!this.gestureListening) return;
    this.gestureListening = false;
    document.removeEventListener('pointerdown', this.onGesture, true);
    document.removeEventListener('keydown', this.onGesture, true);
  }

  /**
   * Create AudioContext + kick off resume() synchronously while user activation is live,
   * then finish preload/drain asynchronously.
   */
  private beginUnlockFromGesture(options?: { playConfirm?: boolean }): Promise<boolean> {
    if (this.permissionSubject.value !== true) {
      return Promise.resolve(false);
    }
    if (this.isUnlocked && this.audioContext?.state === 'running') {
      return Promise.resolve(true);
    }
    if (this.unlockInFlight) {
      return this.unlockInFlight;
    }

    try {
      this.initAudioContextSync();
      if (!this.audioContext) {
        return Promise.resolve(false);
      }

      const resumePromise =
        this.audioContext.state === 'suspended'
          ? this.audioContext.resume()
          : Promise.resolve();

      this.unlockInFlight = this.finishUnlock(resumePromise, options).finally(() => {
        this.unlockInFlight = null;
      });
      return this.unlockInFlight;
    } catch {
      this.isUnlocked = false;
      this.ensureGestureListeners();
      return Promise.resolve(false);
    }
  }

  private async finishUnlock(
    resumePromise: Promise<void>,
    options?: { playConfirm?: boolean }
  ): Promise<boolean> {
    try {
      await resumePromise;
      if (!this.audioContext || this.audioContext.state !== 'running') {
        this.ensureGestureListeners();
        return false;
      }

      await this.preloadBuffers();
      this.isUnlocked = true;
      this.removeGestureListeners();

      if (options?.playConfirm) {
        await this.playBuffer('notification');
      }

      while (this.pendingQueue.length) {
        const key = this.pendingQueue.shift()!;
        await this.playBuffer(key);
      }
      return true;
    } catch {
      this.isUnlocked = false;
      this.ensureGestureListeners();
      return false;
    }
  }

  private async ensureContextRunning(): Promise<void> {
    this.initAudioContextSync();
    if (!this.audioContext) {
      throw new Error('No AudioContext');
    }
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
    if (this.audioContext.state !== 'running') {
      throw new Error('AudioContext not running');
    }
  }

  private async playBuffer(key: SoundKey): Promise<void> {
    await this.loadBuffer(key);
    if (this.audioContext && this.audioBuffers[key]) {
      const source = this.audioContext.createBufferSource();
      source.buffer = this.audioBuffers[key]!;
      source.connect(this.audioContext.destination);
      source.start(0);
      return;
    }
    this.playFallback(key);
  }

  private playFallback(key: SoundKey): void {
    const audio = new Audio(SOUND_FILES[key]);
    void audio.play().catch(() => {});
  }

  private initAudioContextSync(): void {
    if (!this.audioContext) {
      const Ctx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.audioContext = new Ctx();
    }
  }

  private async preloadBuffers(): Promise<void> {
    await Promise.all(SOUND_KEYS.map((key) => this.loadBuffer(key)));
  }

  private async loadBuffer(key: SoundKey): Promise<void> {
    if (this.audioBuffers[key] || !this.audioContext) return;
    try {
      const response = await fetch(SOUND_FILES[key]);
      const arrayBuffer = await response.arrayBuffer();
      this.audioBuffers[key] = await this.audioContext.decodeAudioData(arrayBuffer);
    } catch {
      // Ignore — HTMLAudioElement fallback will be used
    }
  }

  private emitPermissionFromStorage(): void {
    const stored = localStorage.getItem('soundPermissionGranted');
    if (stored === 'true') {
      this.permissionSubject.next(true);
      if (!this.isUnlocked) {
        this.ensureGestureListeners();
      }
    } else if (stored === 'false') {
      this.permissionSubject.next(false);
      this.lockAudio();
    } else {
      this.permissionSubject.next(null);
      this.lockAudio();
    }
  }
}
